import { useState, useEffect, useCallback } from 'react'
import { ArrowLeft, Search, ChevronUp, ChevronDown, X, TrendingUp, Download, FileBarChart } from 'lucide-react'
import { getAPIBaseUrl } from '../../environment'
import './PipelineRunsView.css'

const API_BASE = getAPIBaseUrl()

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function formatDateShort(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// Nodes that produce downloadable Java artifacts.
// Keys are the workflow node types stored in job_mappings (pipeline-* format).
const DOWNLOADABLE_NODES = new Set([
  'pipeline-code-analysis',
  'pipeline-code-refactor',
  'pipeline-service-generation',
  'pipeline-pre-flight',
  'pipeline-java-packaging',
  // v2 MongoDB key format (fallback)
  'code_analysis',
  'code_refactor',
  'service_generation',
  'pre_flight',
  'java_packaging',
])

// Specific file to download per node (undefined = full artifacts dump)
const NODE_FILE_FILTER = {
  'code_analysis': 'generated_java.zip',
  'code_refactor':  'transformed_java_bundle.zip',
  'pipeline-code-analysis': 'generated_java.zip',
  'pipeline-code-refactor':  'transformed_java_bundle.zip',
}

// Specific file to exclude per node
const NODE_FILE_EXCLUDE = {
  'pre_flight': 'java_symbol_index.json',
  'pipeline-pre-flight': 'java_symbol_index.json',
}

// Human-readable labels for pipeline node types
const NODE_LABELS = {
  // v2 MongoDB key format
  'ingest':              'Source Code (Ingest)',
  'code_analysis':       'Code Analysis',
  'data_analysis':       'Data Analysis',
  'code_refactor':       'Refactored Code',
  'monolith_identifier': 'Monolith Identifier',
  'dependency_mapper':   'Dependency Map',
  'ai_naming':           'AI Naming',
  'service_generation':  'Java Services',
  'pre_flight':          'Pre-flight Compile',
  'java_packaging':      'Complete Application',
  'architecture':        'Architecture',
  'discovery_roi':       'Discovery ROI',
  // v3 workflow node type format (pipeline-* keys)
  'pipeline-upload':              'Source Code (Ingest)',
  'pipeline-code-analysis':       'Code Analysis',
  'pipeline-data-analysis':       'Data Analysis',
  'pipeline-code-refactor':       'Refactored Code',
  'pipeline-monolith-identifier': 'Monolith Identifier',
  'pipeline-dependencies':        'Dependency Map',
  'pipeline-ai-naming':           'AI Naming',
  'pipeline-service-generation':  'Java Services',
  'pipeline-pre-flight':          'Pre-flight Compile',
  'pipeline-java-packaging':      'Complete Application',
  'pipeline-architecture':        'Architecture',
  'pipeline-discovery':           'Discovery ROI',
}

function nodeLabel(key) {
  return NODE_LABELS[key] || key.replace(/^pipeline-/i, '').replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// MongoDB status values: running, succeeded, failed
const STATUS_CLS = {
  running:   'status-running',
  succeeded: 'status-succeeded',
  failed:    'status-failed',
}

const FLOW_CLS = {
  workflow: 'flow-workflow',
  hitl:     'flow-journey',
  standard: 'flow-standard',
}

const FLOW_LABELS = {
  workflow: 'Workflow',
  hitl:     'HITL',
  standard: 'Standard',
}

function StatusBadge({ status }) {
  const k = (status || '').toLowerCase()
  return <span className={`pr-badge ${STATUS_CLS[k] || 'status-unknown'}`}>{status || 'Unknown'}</span>
}

function FlowBadge({ flowType }) {
  const k = (flowType || '').toLowerCase()
  const label = FLOW_LABELS[k] || flowType || '—'
  return <span className={`pr-badge ${FLOW_CLS[k] || 'status-unknown'}`}>{label}</span>
}

function SortIcon({ col, sortKey, sortDir }) {
  if (sortKey !== col) return <span className="pr-sort-icon pr-sort-inactive"><ChevronUp size={10} /></span>
  return sortDir === 1
    ? <span className="pr-sort-icon"><ChevronUp size={10} /></span>
    : <span className="pr-sort-icon"><ChevronDown size={10} /></span>
}

export default function PipelineRunsView({ accountId: accountIdProp, onBack, userProfile = {} }) {
  const [resolvedAccountId, setResolvedAccountId] = useState(accountIdProp || null)
  const [runs, setRuns]                   = useState([])
  const [loading, setLoading]             = useState(true)
  const [error, setError]                 = useState(null)
  const [search, setSearch]               = useState('')
  const [statusFilter, setStatusFilter]   = useState('all')
  const [flowFilter, setFlowFilter]       = useState('all')
  const [sortKey, setSortKey]             = useState('started_at')
  const [sortDir, setSortDir]             = useState(-1)
  const [drawer, setDrawer]               = useState(null)
  const [downloading, setDownloading]     = useState(null)
  const [dlError, setDlError]             = useState(null)
  const [jobMeta, setJobMeta]             = useState({})
  const [metaLoading, setMetaLoading]     = useState(false)

  // Resolve account ID: use prop if available, otherwise fetch from /accounts
  useEffect(() => {
    if (accountIdProp) { setResolvedAccountId(accountIdProp); return }
    const token = localStorage.getItem('modernizeit_token')
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    fetch(`${API_BASE}/accounts`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(accounts => {
        if (accounts.length > 0) {
          const def = accounts.find(a => a.isDefault) || accounts[0]
          setResolvedAccountId(def.scoutAccountId)
        }
      })
      .catch(() => {})
  }, [accountIdProp])

  const accountId = resolvedAccountId

  const fetchRuns = useCallback(async () => {
    if (!accountId) return
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem('modernizeit_token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const res = await fetch(
        `${API_BASE}/reporting/runs?account_id=${encodeURIComponent(accountId)}&limit=200`,
        { headers }
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setRuns(data.runs || [])
    } catch (err) {
      console.error('[PipelineRunsView] fetch failed:', err)
      setError('Could not load runs. Check that the API is running.')
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => { fetchRuns() }, [fetchRuns])

  // When drawer opens: fetch the full run doc from MongoDB to get job_ids,
  // then probe each job via SQL /jobs/{id} for status + artifacts availability.
  useEffect(() => {
    if (!drawer) { setJobMeta({}); return }

    setJobMeta({})
    setDlError(null)
    setMetaLoading(true)

    const token = localStorage.getItem('modernizeit_token')
    const headers = token ? { Authorization: `Bearer ${token}` } : {}

    fetch(`${API_BASE}/reporting/runs/${drawer.run_id}`, { headers })
      .then(res => res.ok ? res.json() : null)
      .then(async data => {
        const jobIds = data?.job_ids || {}
        const entries = Object.entries(jobIds).filter(([, id]) => !!id)
        if (entries.length === 0) { setJobMeta({}); setMetaLoading(false); return }

        const results = await Promise.all(
          entries.map(async ([node, jobId]) => {
            try {
              const res = await fetch(`${API_BASE}/jobs/${jobId}`, { headers })
              if (!res.ok) return [node, null]
              const jobData = await res.json()
              const path = jobData.output_data?.artifacts_path || jobData.artifacts_path || null
              return [node, { status: (jobData.status || '').toUpperCase(), hasArtifacts: !!path, jobId }]
            } catch {
              return [node, null]
            }
          })
        )

        const meta = {}
        results.forEach(([node, info]) => { meta[node] = info })
        setJobMeta(meta)
        setMetaLoading(false)
      })
      .catch(() => { setJobMeta({}); setMetaLoading(false) })
  }, [drawer])

  // ── Derived stats ─────────────────────────────────────────────────────────────

  const total     = runs.length
  const succeeded = runs.filter(r => (r.status || '').toLowerCase() === 'succeeded').length
  const failed    = runs.filter(r => (r.status || '').toLowerCase() === 'failed').length
  const running   = runs.filter(r => (r.status || '').toLowerCase() === 'running').length

  // ── Filter + search + sort ────────────────────────────────────────────────────

  let visible = [...runs]

  if (statusFilter !== 'all') visible = visible.filter(r => (r.status    || '').toLowerCase() === statusFilter)
  if (flowFilter   !== 'all') visible = visible.filter(r => (r.flow_type || '').toLowerCase() === flowFilter)

  if (search) {
    const q = search.toLowerCase()
    visible = visible.filter(r => (r.app_name || '').toLowerCase().includes(q))
  }

  visible.sort((a, b) => {
    const av = a[sortKey] ?? ''
    const bv = b[sortKey] ?? ''
    if (av < bv) return -sortDir
    if (av > bv) return  sortDir
    return 0
  })

  const handleSort = (col) => {
    if (sortKey === col) setSortDir(d => d * -1)
    else { setSortKey(col); setSortDir(1) }
  }

  const handleDownload = async (nodeKey, jobId) => {
    if (!jobId || downloading) return
    setDownloading(nodeKey)
    setDlError(null)
    try {
      const token = localStorage.getItem('modernizeit_token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const fileFilter  = NODE_FILE_FILTER[nodeKey]
      const fileExclude = NODE_FILE_EXCLUDE[nodeKey]
      const params = new URLSearchParams()
      if (fileFilter)  params.set('file', fileFilter)
      if (fileExclude) params.set('exclude', fileExclude)
      const qs = params.toString()
      const downloadUrl = `${API_BASE}/artifacts/download/${jobId}${qs ? `?${qs}` : ''}`
      const res = await fetch(downloadUrl, { headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status}${text ? ` — ${text}` : ''}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${nodeKey}_${jobId}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('[PipelineRunsView] download failed:', err)
      setDlError(`Download failed: ${err.message}`)
    } finally {
      setDownloading(null)
    }
  }

  const handleReportDownload = async (jobId) => {
    if (!jobId || downloading) return
    setDownloading('report')
    setDlError(null)
    try {
      const token = localStorage.getItem('modernizeit_token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const res = await fetch(`${API_BASE}/artifacts/reports-bundle/${jobId}`, { headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status}${text ? ` — ${text}` : ''}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const disp = res.headers.get('Content-Disposition') || ''
      const match = disp.match(/filename="?([^"]+)"?/)
      a.download = match ? match[1] : `reports_${jobId}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('[PipelineRunsView] report download failed:', err)
      setDlError(`Report download failed: ${err.message}`)
    } finally {
      setDownloading(null)
    }
  }

  const closeDrawer = () => { setDrawer(null); setDlError(null) }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="pr-view">

      {/* Header */}
      <div className="pr-topbar">
        <div className="pr-topbar-left">
          <button className="pr-back-btn" onClick={onBack}>
            <ArrowLeft size={14} />
            <span>Home</span>
          </button>
          <div className="pr-topbar-title">
            <TrendingUp size={15} />
            <span>Runs</span>
          </div>
        </div>
        <div className="pr-topbar-right">
          <div className="pr-search">
            <Search size={13} />
            <input
              type="text"
              placeholder="Search by project..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pr-content">

        {/* Stat cards */}
        <div className="pr-cards">
          <div className="pr-card">
            <div className="pr-card-label">Total Runs</div>
            <div className="pr-card-value">{loading ? '—' : total}</div>
            <div className="pr-card-sub">All projects</div>
          </div>
          <div className="pr-card pr-card-green">
            <div className="pr-card-label">Succeeded</div>
            <div className="pr-card-value">{loading ? '—' : succeeded}</div>
            <div className="pr-card-sub">{total > 0 ? Math.round(succeeded / total * 100) : 0}% success rate</div>
          </div>
          <div className="pr-card pr-card-red">
            <div className="pr-card-label">Failed</div>
            <div className="pr-card-value">{loading ? '—' : failed}</div>
            <div className="pr-card-sub">{failed > 0 ? 'Needs attention' : 'All clear'}</div>
          </div>
          <div className="pr-card pr-card-amber">
            <div className="pr-card-label">Running</div>
            <div className="pr-card-value">{loading ? '—' : running}</div>
            <div className="pr-card-sub">{running > 0 ? 'In progress' : 'None active'}</div>
          </div>
        </div>

        {/* Filter pills */}
        <div className="pr-filters">
          <span className="pr-filter-label">Status:</span>
          {['all', 'succeeded', 'failed', 'running'].map(f => (
            <button
              key={f}
              className={`pr-filter-btn ${statusFilter === f ? 'active' : ''}`}
              onClick={() => setStatusFilter(f)}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <span className="pr-filter-label pr-filter-label-sep">Flow:</span>
          {[
            { key: 'all',      label: 'All'      },
            { key: 'workflow', label: 'Workflow'  },
            { key: 'hitl',     label: 'HITL'     },
            { key: 'standard', label: 'Standard' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`pr-filter-btn ${flowFilter === key ? 'active' : ''}`}
              onClick={() => setFlowFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="pr-section">
          <div className="pr-section-header">
            <span className="pr-section-title">Pipeline Runs</span>
            <span className="pr-section-count">{visible.length} run{visible.length !== 1 ? 's' : ''}</span>
          </div>

          {error && (
            <div className="pr-error">{error}</div>
          )}

          {!error && (
            <table className="pr-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('app_name')} className="pr-th-sortable">
                    Project <SortIcon col="app_name" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th>Flow</th>
                  <th onClick={() => handleSort('started_at')} className="pr-th-sortable">
                    Started <SortIcon col="started_at" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('completed_at')} className="pr-th-sortable">
                    Completed <SortIcon col="completed_at" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('status')} className="pr-th-sortable">
                    Status <SortIcon col="status" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {!loading && visible.length === 0 && (
                  <tr>
                    <td colSpan={5} className="pr-empty-cell">
                      {search || statusFilter !== 'all' || flowFilter !== 'all'
                        ? 'No runs match the current filters.'
                        : 'No runs found for this account.'}
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan={5} className="pr-empty-cell">Loading...</td>
                  </tr>
                )}
                {!loading && visible.map((run, i) => (
                  <tr key={run.run_id || i} className="pr-row" onClick={() => setDrawer(run)}>
                    <td><span className="pr-td-link">{run.app_name || '—'}</span></td>
                    <td><FlowBadge flowType={run.flow_type} /></td>
                    <td className="pr-td-meta">{formatDateShort(run.started_at)}</td>
                    <td className="pr-td-meta">{formatDateShort(run.completed_at)}</td>
                    <td><StatusBadge status={run.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* Drawer */}
      {drawer && (
        <div className="pr-drawer-overlay" onClick={closeDrawer}>
          <div className="pr-drawer" onClick={e => e.stopPropagation()}>
            <div className="pr-drawer-header">
              <span className="pr-drawer-title">{drawer.app_name || 'Run Detail'}</span>
              <button className="pr-drawer-close" onClick={closeDrawer}><X size={16} /></button>
            </div>
            <div className="pr-drawer-body">

              <div className="pr-drawer-row">
                <StatusBadge status={drawer.status} />
                <FlowBadge flowType={drawer.flow_type} />
              </div>

              <div className="pr-drawer-label">Run ID</div>
              <div className="pr-drawer-value pr-mono">{drawer.run_id || '—'}</div>

              <div className="pr-drawer-label">Started</div>
              <div className="pr-drawer-value">{formatDate(drawer.started_at)}</div>

              {drawer.completed_at && (
                <>
                  <div className="pr-drawer-label">Completed</div>
                  <div className="pr-drawer-value">{formatDate(drawer.completed_at)}</div>
                </>
              )}

              {drawer.nodes_completed && drawer.nodes_completed.length > 0 && (
                <>
                  <div className="pr-drawer-label">Nodes Completed</div>
                  <div className="pr-drawer-nodes">
                    {drawer.nodes_completed.map(n => (
                      <span key={n} className="pr-node-chip">{nodeLabel(n)}</span>
                    ))}
                  </div>
                </>
              )}

              <div className="pr-drawer-label">Download by Layer</div>

              {dlError && (
                <div className="pr-dl-error">{dlError}</div>
              )}

              {metaLoading ? (
                <div className="pr-drawer-value pr-drawer-empty">Checking artifacts…</div>
              ) : (() => {
                const available = Object.entries(jobMeta).filter(([node, m]) =>
                  m && m.status === 'COMPLETED' && m.hasArtifacts && DOWNLOADABLE_NODES.has(node)
                )
                if (available.length === 0) {
                  return <div className="pr-drawer-value pr-drawer-empty">No downloadable artifacts for this run.</div>
                }
                return (
                  <div className="pr-download-list">
                    {available.map(([node, meta]) => {
                      const isDisabled = !!downloading
                      return (
                        <div key={node} className="pr-download-row">
                          <span className="pr-download-label">{nodeLabel(node)}</span>
                          <button
                            className={`pr-download-btn ${downloading === node ? 'pr-download-btn-busy' : ''}`}
                            disabled={isDisabled}
                            onClick={() => handleDownload(node, meta.jobId)}
                          >
                            <Download size={12} />
                            {downloading === node ? 'Downloading…' : 'Download'}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )
              })()}

              {/* Reports — available when Code Analysis has completed */}
              {(() => {
                const caEntry = Object.entries(jobMeta).find(([node, m]) =>
                  m && m.status === 'COMPLETED' &&
                  (node === 'pipeline-code-analysis' || node === 'code_analysis')
                )
                if (!caEntry) return null
                const [, caMeta] = caEntry
                return (
                  <>
                    <div className="pr-drawer-label" style={{ marginTop: 16 }}>Reports</div>
                    <div className="pr-download-list">
                      <div className="pr-download-row">
                        <span className="pr-download-label">
                          <FileBarChart size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
                          Dashboard Report
                        </span>
                        <button
                          className={`pr-download-btn ${downloading === 'report' ? 'pr-download-btn-busy' : ''}`}
                          disabled={!!downloading}
                          onClick={() => handleReportDownload(caMeta.jobId)}
                        >
                          <Download size={12} />
                          {downloading === 'report' ? 'Generating…' : 'Download Reports'}
                        </button>
                      </div>
                    </div>
                  </>
                )
              })()}

            </div>
          </div>
        </div>
      )}

    </div>
  )
}
